// 2026-07-23 - FormuLearning - Step 4 배치별 f2 추이 차트 (이슈 #50 S-1, P4-4)
// 파일 위치: frontend/src/features/step4/BatchF2Chart.tsx
// 2026-08-03 수정 - 이슈 #52: 표가 전치되어 선택 단위가 행 → **열**이 됐다(문구만 조정).
// 2026-09-21 수정 - 이슈 #69(S6, COMP-F2-TREND-CHART · M84-22·26 · Q-260516-02):
//                   ① 단일 선(대표값) → **용출액별 3커브 한 그래프**(D2 — 대표값은 hover·성분표에 남김)
//                   ② **포인트 클릭 = 배치 선택 + 그 계열 강조**, 범례 클릭 = 강조 토글, 빈 곳 클릭
//                      = 해제(D3). plotly 기본 «범례 클릭 = 숨기기» 는 쓰지 않는다
//                   ③ **보고 있는 라운드 구간 음영**(M84-26)
//                   ④ x축 = 제외되지 않은 **전 배치**(결과 없는 배치 포함) — optimization run 직후에도
//                      **x축만** 그린다. 종전에는 포인트가 없으면 아무것도 그리지 않았다(M84-26)
//                   색 = 앱 토큰 teal·amber·purple(용액 정렬 순 고정) + 마커 모양 보조 부호(일지 I7).
// 목적: 가로축 = batch_code(구 iteration 폐기), 세로 = f2. 통합표 바로 아래에 놓여
//       포인트 클릭 ↔ 표 열 클릭이 같은 선택 상태를 공유한다(상호 하이라이트).
//       **전 라운드를 가로지르는 단 하나의 그래프**다 — 상위에서 라운드로 거르지 않는다(D-7).

import { useEffect, useRef, useState } from 'react'
import type { LotBatch } from '../../api/lot'
import { useUiStore } from '../../stores/uiStore'
import { chartTokens } from '../../lib/chartTheme'
import { compareSolutionKeys, solutionLabel } from '../../lib/dissolution'

const PLOT_OPTS = { displayModeBar: false, responsive: true }
/** 계열 보조 부호 — 색만으로 계열을 가르지 않는다(색각 이상·흑백 인쇄). */
const SYMBOLS = ['circle', 'square', 'diamond', 'triangle-up']

interface Props {
  /** 전 라운드 배치(라운드 필터 예외, UI/UX v2.3 D-7). */
  batches: LotBatch[]
  /** 보고 있는 라운드 — 그 구간을 음영으로 표시한다. */
  currentRoundId: string | null
  selectedId: string | null
  onSelect: (batchId: string) => void
  targetValue: number | null
}

/** plotly 이벤트를 받는 DOM 노드 타입(간이). */
type PlotlyEl = HTMLDivElement & {
  on?: (event: string, cb: (data: PlotlyEventData) => unknown) => void
  removeAllListeners?: (event: string) => void
}
interface PlotlyEventData {
  points?: { x?: unknown; curveNumber?: number }[]
  curveNumber?: number
}

export default function BatchF2Chart({ batches, currentRoundId, selectedId, onSelect, targetValue }: Props) {
  const theme = useUiStore((s) => s.theme)
  const ref = useRef<HTMLDivElement>(null)
  // 강조 계열(용액 키). null = 강조 없음(전 계열 동등).
  const [focus, setFocus] = useState<string | null>(null)
  // plotly 가 점·범례 클릭을 처리한 직후의 DOM click 은 «빈 곳 클릭» 이 아니다.
  const plotHit = useRef(false)

  // x축 = 제외되지 않은 전 배치(표와 같은 순서). 제외 배치는 f2 = null 이라 뺀다(BR-S4-L10).
  const axis = batches.filter((b) => !b.is_excluded)
  const solutions = [...new Set(axis.flatMap((b) => Object.keys(b.f2_by_solution ?? {})))].sort(compareSolutionKeys)
  const hasPoints = solutions.length > 0
  const activeFocus = focus && solutions.includes(focus) ? focus : null

  useEffect(() => {
    if (!ref.current || axis.length === 0) return
    let cancelled = false
    import('plotly.js-dist-min').then((Plotly) => {
      if (cancelled || !ref.current) return
      const t = chartTokens()
      const palette = [t.teal, t.amber, t.purple, t.font]
      const codes = axis.map((b) => b.batch_code)
      const selIdx = axis.findIndex((b) => b.id === selectedId)

      const traces: Record<string, unknown>[] = solutions.map((key, i) => {
        const dim = activeFocus != null && activeFocus !== key
        return {
          x: codes,
          y: axis.map((b) => b.f2_by_solution?.[key] ?? null),
          name: solutionLabel(key),
          meta: key,
          type: 'scatter', mode: 'lines+markers',
          connectgaps: false,   // 결과 없는 배치 자리는 선을 끊는다 — 없는 값을 잇지 않는다
          opacity: dim ? 0.2 : 1,
          line: { color: palette[i % palette.length], width: activeFocus === key ? 3 : 2 },
          marker: {
            color: palette[i % palette.length], symbol: SYMBOLS[i % SYMBOLS.length],
            size: axis.map((b) => (b.id === selectedId ? 12 : 8)),
          },
          // 대표값(용액별 최솟값, BR-S4-L08)은 선이 아니라 hover 로 남긴다(D2)
          customdata: axis.map((b) => b.f2),
          hovertemplate: `%{x} · ${solutionLabel(key)}<br>f2 %{y:.1f}<br>f2 (min) %{customdata:.1f}<extra></extra>`,
        }
      })
      // 포인트가 하나도 없어도 x축(배치 코드)은 세운다 — run 직후 빈 화면 금지(M84-26)
      if (!hasPoints) {
        traces.push({ x: codes, y: codes.map(() => null), type: 'scatter', mode: 'markers',
                      showlegend: false, hoverinfo: 'skip' })
      }

      const shapes: Record<string, unknown>[] = []
      const annotations: Record<string, unknown>[] = []
      // 보고 있는 라운드 구간 음영(M84-26). 라운드 배치가 떨어져 있으면 구간마다 칠한다.
      let start = -1
      axis.forEach((b, i) => {
        const inRound = currentRoundId != null && b.round_id === currentRoundId
        const nextIn = i + 1 < axis.length && currentRoundId != null && axis[i + 1].round_id === currentRoundId
        if (inRound && start < 0) start = i
        if (inRound && !nextIn) {
          shapes.push({ type: 'rect', xref: 'x', yref: 'paper', x0: start - 0.5, x1: i + 0.5, y0: 0, y1: 1,
                        fillcolor: t.teal, opacity: 0.09, line: { width: 0 }, layer: 'below' })
          annotations.push({ xref: 'x', yref: 'paper', x: (start + i) / 2, y: 1, yanchor: 'bottom',
                             text: `Round ${b.round_no ?? ''}`, showarrow: false,
                             font: { size: 9, color: t.font }, opacity: 0.7 })
          start = -1
        }
      })
      // 선택 배치 세로선 — 세 계열 중 어느 점이든 같은 배치임을 보인다
      if (selIdx >= 0) {
        shapes.push({ type: 'line', xref: 'x', yref: 'paper', x0: selIdx, x1: selIdx, y0: 0, y1: 1,
                      line: { color: t.font, width: 1, dash: 'dot' }, opacity: 0.35 })
      }
      // 동등 기준선(FDA 50) — 계열 색과 겹치지 않게 중립색
      if (targetValue != null) {
        shapes.push({ type: 'line', xref: 'paper', yref: 'y', x0: 0, x1: 1, y0: targetValue, y1: targetValue,
                      line: { color: t.font, width: 1, dash: 'dash' }, opacity: 0.45 })
      }
      if (!hasPoints) {
        // 기준선(50)과 겹치지 않는 위쪽에 둔다
        annotations.push({ xref: 'paper', yref: 'paper', x: 0.5, y: 0.82, showarrow: false,
                           text: '결과를 입력하면 용출액별 f2가 표시됩니다', font: { size: 10, color: t.font }, opacity: 0.6 })
      }

      Plotly.newPlot(ref.current, traces, {
        paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: t.plotBg,
        font: { color: t.font, size: 10 },
        margin: { t: 34, r: 15, b: 60, l: 40 },
        hovermode: 'closest',
        xaxis: { type: 'category', categoryorder: 'array', categoryarray: codes,
                 gridcolor: t.grid, tickangle: -35, fixedrange: true },
        yaxis: { gridcolor: t.grid, title: { text: 'f2' }, fixedrange: true,
                 ...(hasPoints ? {} : { range: [0, 100] }) },
        shapes, annotations,
        showlegend: hasPoints,
        legend: { orientation: 'h', x: 0, y: 1.02, yanchor: 'bottom', font: { size: 9 } },
      }, PLOT_OPTS).then(() => {
        const el = ref.current as PlotlyEl | null
        if (!el?.on) return
        // newPlot 을 다시 부를 때 이전 핸들러가 쌓이지 않게 비운다(토글이 두 번 걸리면 상쇄된다)
        ;['plotly_click', 'plotly_legendclick', 'plotly_legenddoubleclick'].forEach((e) => el.removeAllListeners?.(e))
        // 포인트 클릭 → 그 배치 선택(열 하이라이트·라운드 전환) + 그 계열 강조(D3)
        el.on('plotly_click', (data) => {
          plotHit.current = true
          const p = data.points?.[0]
          const hit = axis.find((b) => b.batch_code === p?.x)
          if (hit) onSelect(hit.id)
          const key = p?.curveNumber != null ? solutions[p.curveNumber] : undefined
          if (key) setFocus(key)
        })
        // 범례 클릭 → 강조 토글. false 를 돌려 plotly 기본 동작(계열 숨기기)을 막는다
        el.on('plotly_legendclick', (data) => {
          plotHit.current = true
          const key = data.curveNumber != null ? solutions[data.curveNumber] : undefined
          if (key) setFocus((f) => (f === key ? null : key))
          return false
        })
        el.on('plotly_legenddoubleclick', () => false)
      })
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(axis.map((b) => [b.id, b.f2, b.f2_by_solution, b.round_id])),
      selectedId, currentRoundId, activeFocus, targetValue, theme])

  if (axis.length === 0) return null
  return (
    <div className="mt-2" data-testid="batch-f2-chart" data-focus={activeFocus ?? ''}>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <p className="text-[10px] uppercase tracking-wider text-text-secondary">
          f2 by batch{' '}
          <span className="normal-case text-text-muted">
            — 포인트 클릭 = 배치 선택·계열 강조 · 범례 클릭 = 계열 강조 · 빈 곳 클릭 = 해제
          </span>
        </p>
        {activeFocus && (
          <button
            type="button"
            onClick={() => setFocus(null)}
            data-testid="f2-focus-clear"
            className="rounded px-1.5 py-0.5 text-[10px] text-text-secondary hover:bg-bg-input"
            style={{ border: '1px solid var(--border-subtle)' }}
          >
            강조: {solutionLabel(activeFocus)} ✕
          </button>
        )}
      </div>
      <div
        ref={ref}
        style={{ width: '100%', height: 260 }}
        onClick={() => {
          // 점·범례를 누른 클릭이 아니면 «빈 곳 클릭» — 강조를 푼다(D3)
          if (plotHit.current) plotHit.current = false
          else setFocus(null)
        }}
      />
    </div>
  )
}
